export default function AIAnswerBlock() {
  return (
    <section
      className="py-28 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #0c2236 0%, #0d3528 40%, #0F2A44 70%, #0c2236 100%)' }}
    >
      {/* Background decorative rings */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full border border-white/[0.04] translate-x-48 -translate-y-48 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full border border-white/[0.04] -translate-x-24 translate-y-24 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full border border-white/[0.02] pointer-events-none" />
      {/* Gold glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(200,169,106,0.06) 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="inline-block w-10 h-px bg-gold opacity-60"></span>
            <p className="text-gold text-xs font-bold uppercase tracking-[0.2em]">About Our Care</p>
            <span className="inline-block w-10 h-px bg-gold opacity-60"></span>
          </div>
          <h2 className="font-heading text-white font-bold mb-5 leading-tight" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 3.2rem)' }}>
            What Does Oak Senior Care Provide?
          </h2>
          <p className="text-white/55 text-base leading-relaxed max-w-3xl mx-auto">
            Oak Senior Care provides professional in-home caregiving services in Oakland, IL designed to support seniors
            with daily living, companionship, and specialized care needs. Every service is customized to each client,
            helping seniors remain{' '}
            <strong className="text-white/85 font-semibold">safe, independent, and comfortable at home</strong>.
          </p>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {/* Services Offered */}
          <div
            className="rounded-2xl p-8 border border-white/[0.08] relative overflow-hidden group hover:border-white/15 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gold/[0.05] rounded-full translate-x-8 -translate-y-8 pointer-events-none" />
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/20">
                <i className="ri-service-line text-gold text-lg"></i>
              </div>
              <h3 className="font-heading text-white text-xl font-bold">Services Offered</h3>
            </div>
            <ul className="space-y-3.5">
              {[
                'Personal care & daily living support',
                "Memory care for Alzheimer's & dementia",
                'Companion care & social engagement',
                'Recovery support after hospitalization',
                'Respite care for family caregivers',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/65 text-sm leading-relaxed">
                  <i className="ri-checkbox-circle-fill text-sage mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Benefits */}
          <div
            className="rounded-2xl p-8 border border-white/[0.08] relative overflow-hidden hover:border-white/15 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-sage/[0.05] rounded-full -translate-x-8 translate-y-8 pointer-events-none" />
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/20">
                <i className="ri-heart-3-fill text-gold text-lg"></i>
              </div>
              <h3 className="font-heading text-white text-xl font-bold">Key Benefits</h3>
            </div>
            <ul className="space-y-3.5">
              {[
                'Maintain independence at home',
                'Reduce family caregiver stress & burnout',
                'Improve overall quality of life',
                'Professional, dignified care every day',
                'Peace of mind for the whole family',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-white/65 text-sm leading-relaxed">
                  <i className="ri-checkbox-circle-fill text-sage mt-0.5 flex-shrink-0"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Process */}
          <div
            className="rounded-2xl p-8 border border-white/[0.08] relative overflow-hidden hover:border-white/15 transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            <div className="flex items-center gap-3 mb-7">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold/20">
                <i className="ri-route-fill text-gold text-lg"></i>
              </div>
              <h3 className="font-heading text-white text-xl font-bold">Our Process</h3>
            </div>
            <div className="relative">
              <div className="absolute left-4 top-4 bottom-4 w-px bg-white/10" />
              <div className="space-y-5">
                {[
                  { step: '01', label: 'Free Care Consultation', desc: 'Discuss needs at no cost or obligation' },
                  { step: '02', label: 'Custom Care Plan', desc: 'Tailored specifically for your family' },
                  { step: '03', label: 'Caregiver Match', desc: 'Carefully selected for best compatibility' },
                  { step: '04', label: 'Ongoing Support', desc: 'Regular check-ins and plan adjustments' },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 pl-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0F2A44] border-2 border-gold/50 flex-shrink-0 z-10 relative">
                      <span className="font-heading text-gold text-[10px] font-bold">{item.step}</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold leading-snug">{item.label}</p>
                      <p className="text-white/40 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
          {[
            { number: '500+', label: 'Families Served', icon: 'ri-home-heart-line' },
            { number: '24/7', label: 'Care Available', icon: 'ri-time-fill' },
            { number: '5★', label: 'Google Rating', icon: 'ri-star-fill' },
            { number: 'Same-Week', label: 'Care Start', icon: 'ri-calendar-check-line' },
          ].map((stat, i) => (
            <div key={stat.label} className={`text-center px-6 py-7 bg-[rgba(255,255,255,0.03)] ${i < 3 ? '' : ''}`}>
              <div className="w-9 h-9 flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-gold text-2xl`}></i>
              </div>
              <p className="font-heading text-white text-2xl font-bold">{stat.number}</p>
              <p className="text-white/45 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
